// ============================================================================
// Google Calendar — capability glue.
//
// Bridges the Google-specific helpers in `calendars.ts` / `events.ts` /
// `watch.ts` into the shape the capability registry expects
// (`AdapterMethods<'calendar'>`).
//
// Usage (registration site in `index.ts`):
//   import { buildGoogleCalendarMethods } from './capability-methods'
//   register('google-calendar', 'calendar', buildGoogleCalendarMethods({}))
//
// The `ctx` argument on each method is the `AdapterContext` the dispatcher
// hands in — carrying the decrypted access token + per-instance config JSON.
// ============================================================================

import type { AdapterMethods } from '../../services/capability-registry'
import type {
  Calendar,
  CalendarEvent,
  ListEventsResult,
  SubscribeChangesArgs,
  SubscribeChangesResult,
} from '../../capabilities/calendar'
import { listGoogleCalendars } from './calendars'
import {
  createGoogleEvent,
  deleteGoogleEvent,
  getGoogleEvent,
  listGoogleEvents,
  updateGoogleEvent,
} from './events'
import {
  subscribeCalendarChanges,
  unsubscribeCalendarChannel,
} from './watch'

// ============================================================================
// Dependencies
// ============================================================================

export interface GoogleCalendarMethodsDeps {
  /**
   * Injectable fetch for tests. Defaults to the global `fetch`. Keep this as
   * the ONLY moving part so registration in production stays trivial.
   */
  fetchImpl?: typeof fetch
  /**
   * Optional structured logger. Defaults to no-op. Used for non-fatal
   * bookkeeping logs (e.g. swallowed-but-logged errors). API errors surface
   * as `GoogleCalendarError` throws, independent of the logger.
   */
  logger?: {
    error?: (msg: string, extra?: Record<string, unknown>) => void
  }
}

// ============================================================================
// buildGoogleCalendarMethods
// ============================================================================

export function buildGoogleCalendarMethods(
  deps: GoogleCalendarMethodsDeps = {},
): AdapterMethods<'calendar'> {
  const fetchImpl = deps.fetchImpl ?? fetch

  return {
    async listCalendars(_args, ctx): Promise<Calendar[]> {
      return listGoogleCalendars(ctx.accessToken, fetchImpl)
    },

    async listEvents(args, ctx): Promise<ListEventsResult> {
      return listGoogleEvents(
        {
          accessToken: ctx.accessToken,
          calendarId: args.calendarId,
          ...(args.timeMin ? { timeMin: args.timeMin } : {}),
          ...(args.timeMax ? { timeMax: args.timeMax } : {}),
          ...(args.pageToken ? { pageToken: args.pageToken } : {}),
          ...(typeof args.maxResults === 'number' ? { maxResults: args.maxResults } : {}),
          ...(args.syncToken ? { syncToken: args.syncToken } : {}),
        },
        fetchImpl,
      )
    },

    async getEvent(args, ctx): Promise<CalendarEvent> {
      return getGoogleEvent(
        {
          accessToken: ctx.accessToken,
          calendarId: args.calendarId,
          eventId: args.eventId,
        },
        fetchImpl,
      )
    },

    async createEvent(args, ctx): Promise<CalendarEvent> {
      return createGoogleEvent(
        {
          accessToken: ctx.accessToken,
          calendarId: args.calendarId,
          event: args.event,
        },
        fetchImpl,
      )
    },

    async updateEvent(args, ctx): Promise<CalendarEvent> {
      return updateGoogleEvent(
        {
          accessToken: ctx.accessToken,
          calendarId: args.calendarId,
          eventId: args.eventId,
          patch: args.patch,
        },
        fetchImpl,
      )
    },

    async deleteEvent(args, ctx): Promise<void> {
      await deleteGoogleEvent(
        {
          accessToken: ctx.accessToken,
          calendarId: args.calendarId,
          eventId: args.eventId,
        },
        fetchImpl,
      )
    },

    async subscribeChanges(args: SubscribeChangesArgs, ctx): Promise<SubscribeChangesResult> {
      // `subscribeCalendarChanges` mints a fresh channel id + channel token
      // when the caller omits them. We always omit here so every subscribe
      // call gets unique secrets — the calling chat/calendar module is
      // responsible for persisting the returned `channelToken` into
      // `connector_instances.config` so `verifyGoogleCalendarSignature` can
      // later validate inbound webhooks. We return the token on the
      // capability-level shape under a non-standard key so callers know it
      // came from this adapter.
      const out = await subscribeCalendarChanges(
        {
          accessToken: ctx.accessToken,
          calendarId: args.calendarId,
          webhookUrl: args.webhookUrl,
          ...(args.expiration ? { expiration: args.expiration } : {}),
        },
        fetchImpl,
      )

      // Google-specific extension: surface the channelToken so the caller can
      // persist it. The capability-level `SubscribeChangesResult` doesn't
      // model it (it's provider-specific), but TypeScript permits the extra
      // property on an object literal returned from a function typed with
      // the narrower interface.
      return {
        channelId: out.channelId,
        resourceId: out.resourceId,
        expiration: out.expiration,
        // Extra provider-specific field. Consumers that know they're
        // talking to Google Calendar can read `.channelToken` off the
        // result; others will simply ignore it.
        channelToken: out.channelToken,
      } as SubscribeChangesResult & { channelToken: string }
    },

    async unsubscribeChanges(args, ctx): Promise<void> {
      await unsubscribeCalendarChannel(
        {
          accessToken: ctx.accessToken,
          channelId: args.channelId,
          resourceId: args.resourceId,
        },
        fetchImpl,
      )
    },
  }
}
